/**
 * URLs, without a router library.
 *
 * The app has four places you can be, and the browser already knows how to
 * store a location, go back, and let someone paste an address to a colleague.
 * A routing library would add a dependency and a mental model to reimplement
 * what `history.pushState` does in twenty lines.
 *
 * This exists because a link to a task is how work actually gets handed over —
 * pasted into a Teams chat next to "can you look at this". Navigation held in
 * React state cannot be pasted anywhere, and also breaks refresh and the Back
 * button, which people try without thinking.
 */
import { useEffect, useState } from "react";

export type Route =
  | { readonly kind: "home" }
  | { readonly kind: "projects" }
  | { readonly kind: "project"; readonly id: string }
  | { readonly kind: "deliverable"; readonly id: string }
  | { readonly kind: "inbox" }
  /** Finished work (punch list F117, 30 Oct 2026): the stages I finished, searched by date. */
  | { readonly kind: "finished" }
  | { readonly kind: "workload" }
  | { readonly kind: "overview" }
  | { readonly kind: "attention" }
  | { readonly kind: "admin" }
  /**
   * The punch list (Jeremy, 27 Oct 2026): /punchlist, and /punchlist/F94 with that row open.
   * Members only; the API refuses everybody else, as it does /admin.
   */
  | { readonly kind: "punchlist"; readonly label: string | null }
  | { readonly kind: "task"; readonly id: string }
  /**
   * /mobilizations/<deployment id>: the resource scheduler's "Open in Fieldpath" (21 Oct 2026).
   * Looked up and REPLACED by the task linked to that deployment, or says none is linked yet.
   */
  | { readonly kind: "mobilization"; readonly id: string }
  /**
   * In-app help (Jeremy, 19 Oct 2026; built 22 Oct). /help is every article, /help/<id> one of
   * them, and /help/for/<screen> the articles for a screen: what a ? opens, including the
   * resource scheduler's, which cannot know Fieldpath's article ids and so names its screen.
   */
  | { readonly kind: "help"; readonly id: string | null; readonly screen: string | null };

/**
 * Ids are checked against the shape of a UUID before being treated as one.
 * Without it, "/tasks/favicon.ico" becomes a database lookup and an error
 * message, rather than simply not being a task route.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A help article's id (E5, P12, A3), and a help screen name (task, scheduler-calendar). */
const HELP_ID = /^[EPA]\d{1,3}$/i;
const HELP_SCREEN = /^[a-z][a-z0-9-]{0,40}$/;
/** A punch-list row: F for Fieldpath, S for the scheduler, and its number. */
const PUNCH_LABEL = /^[FS]\d{1,6}$/i;

export function parseRoute(pathname: string): Route {
  const [first, second, third] = pathname.replace(/^\/+|\/+$/g, "").split("/");

  if (first === "tasks" && second && UUID.test(second)) {
    return { kind: "task", id: second };
  }
  if (first === "projects" && second && UUID.test(second)) {
    return { kind: "project", id: second };
  }
  if (first === "mobilizations" && second && UUID.test(second)) {
    return { kind: "mobilization", id: second };
  }
  if (first === "deliverables" && second && UUID.test(second)) {
    return { kind: "deliverable", id: second };
  }
  if (first === "help") {
    // Shape-checked like the ids above: an unknown id or screen is the help index, not an error.
    if (second === "for" && third && HELP_SCREEN.test(third)) return { kind: "help", id: null, screen: third };
    if (second && HELP_ID.test(second)) return { kind: "help", id: second.toUpperCase(), screen: null };
    return { kind: "help", id: null, screen: null };
  }
  if (first === "projects") return { kind: "projects" };
  if (first === "inbox") return { kind: "inbox" };
  if (first === "finished") return { kind: "finished" };
  if (first === "workload") return { kind: "workload" };
  if (first === "overview") return { kind: "overview" };
  if (first === "attention") return { kind: "attention" };
  // The route exists for anybody; the API refuses anybody who is not an
  // admin. Hiding the path would be security by obscurity, and the server
  // is where the rule belongs.
  if (first === "admin") return { kind: "admin" };
  if (first === "punchlist") {
    return { kind: "punchlist", label: second && PUNCH_LABEL.test(second) ? second.toUpperCase() : null };
  }

  // Anything unrecognised is home rather than a 404 page. A stale or mistyped
  // link should land somewhere useful, not at a dead end.
  return { kind: "home" };
}

export function pathFor(route: Route): string {
  switch (route.kind) {
    case "task":
      return `/tasks/${route.id}`;
    case "project":
      return `/projects/${route.id}`;
    case "deliverable":
      return `/deliverables/${route.id}`;
    case "mobilization":
      return `/mobilizations/${route.id}`;
    case "projects":
      return "/projects";
    case "inbox":
      return "/inbox";
    case "finished":
      return "/finished";
    case "workload":
      return "/workload";
    case "overview":
      return "/overview";
    case "attention":
      return "/attention";
    case "admin":
      return "/admin";
    case "punchlist":
      return route.label ? `/punchlist/${route.label}` : "/punchlist";
    case "help":
      if (route.id) return `/help/${route.id}`;
      return route.screen ? `/help/for/${route.screen}` : "/help";
    case "home":
      return "/";
  }
}

/** The full address to hand to someone else. */
export function shareUrl(route: Route): string {
  return `${window.location.origin}${pathFor(route)}`;
}

/**
 * pushState does not fire popstate — that event is only for the user moving
 * through history — so in-app navigation announces itself.
 */
const ROUTE_EVENT = "fieldpath:route";

/**
 * To an in-app PATH that may carry a `#place`, such as a What's new notice's `/help/E23#v1-1`
 * (F149). The path is the route; the hash only says where on the page.
 */
export function navigateToPath(path: string): void {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new Event(ROUTE_EVENT));
}

export function navigate(route: Route, replace = false): void {
  const path = pathFor(route);
  if (path !== window.location.pathname) {
    // Replace, for an address that only stands for another (/mobilizations/<id> -> the task),
    // so Back does not bounce through it.
    if (replace) window.history.replaceState(null, "", path);
    else window.history.pushState(null, "", path);
  }
  window.dispatchEvent(new Event(ROUTE_EVENT));
}

/** Where the browser currently is, kept in step with Back and Forward. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const sync = (): void => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", sync);
    window.addEventListener(ROUTE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(ROUTE_EVENT, sync);
    };
  }, []);

  return route;
}

/**
 * Carrying the destination across sign-in.
 *
 * Entra returns to the registered redirect URI, which is the site root — so
 * someone opening a task link while signed out would sign in successfully and
 * land on the home page, having lost the thing they clicked. The path is
 * stashed before the redirect and put back afterwards.
 *
 * MSAL has its own mechanism for this, but it is doing the navigating rather
 * than us, and relying on it would mean a broken deep link is only discovered
 * by a real person signing in. This is explicit and checkable.
 */
const RETURN_KEY = "fieldpath:returnTo";

export function rememberReturnPath(): void {
  // The hash too: a What's new link names the version's place in the article (F149).
  const path = window.location.pathname + window.location.search + window.location.hash;
  if (path === "/") return;
  try {
    window.sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    // Private browsing or blocked storage. Sign-in still works; it just lands
    // at home, which is a worse outcome than a crash is.
  }
}

export function restoreReturnPath(): void {
  let saved: string | null = null;
  try {
    saved = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    return;
  }

  // Only when sitting at the root. If the path survived the round trip by
  // some other route, overwriting it would undo a correct answer.
  if (saved && saved !== "/" && window.location.pathname === "/") {
    window.history.replaceState(null, "", saved);
  }
}

/**
 * True only once something has been loading for a moment.
 *
 * A fetch that finishes in 40ms should show nothing at all: a spinner that
 * appears and vanishes reads as the app stuttering, and is worse than a brief
 * pause on the previous screen. Past the delay a spinner is reassurance
 * rather than noise.
 */
export function useSettledLoading(active: boolean, delayMs = 180): boolean {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const timer = window.setTimeout(() => setShown(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, delayMs]);

  return shown;
}
